import {
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  useAlert
} from "@prismicio/editor-ui"
import { Suspense, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { useRouteParams } from "../../app/routing"
import { useRepositoryReleases } from "../../document/documentRouting"
import { DocumentListBody } from "../DocumentListBody"
import { DocumentListBodyLayout } from "../DocumentListBody/DocumentListBody"
import { DocumentListHeader } from "../DocumentListHeader"
import { DocumentListLayout } from "../documentListLayout"
import { DocumentListSidePanel } from "../DocumentListSidePanel"
import { DocumentListProvider } from "../useDocumentList"
import { ReleaseDate } from "./ReleaseDate"
import { ReleaseDeleteButton } from "./ReleaseDeleteButton"
import { ReleaseNameInput } from "./ReleaseNameInput"
import {
  ReleasePageHeader,
  ReleasePageHeaderSkeleton
} from "./ReleasePageHeader"
import { ReleasePreview } from "./ReleasePreview"
import { ReleaseStatusIndicator } from "./ReleaseStatusIndicator"

export function ReleasePage() {
  return (
    <ErrorBoundary renderError={renderReleasePageError}>
      <Suspense fallback={<ReleasePageSkeleton />}>
        <ReleasePageLayout />
      </Suspense>
    </ErrorBoundary>
  )
}

function ReleasePageLayout() {
  useRedirectOnNonExistentRelease()

  return (
    <DocumentListProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel />}
        // TODO: Implement ReleasePage body
        // https://linear.app/prismic/issue/PBD-1415/specific-release-page-document-list
        body={
          <ErrorBoundary renderError={renderReleasePageError}>
            <Suspense fallback={<ReleasePageSkeleton />}>
              <ReleasePageBody />
            </Suspense>
          </ErrorBoundary>
        }
      />
    </DocumentListProvider>
  )
}

function ReleasePageBody() {
  const { release } = useReleaseFromRouteParamId()

  return (
    <DocumentListBodyLayout>
      <ErrorBoundary renderError={renderReleasePageError}>
        <Suspense fallback={<ReleasePageHeaderSkeleton />}>
          <ReleasePageHeader
            title={
              <>
                <ReleaseStatusIndicator release={release} />
                <ReleaseNameInput
                  defaultValue={release?.label}
                  releaseId={release?.id}
                />
              </>
            }
            // TODO: Implement actions
            // 1. Publish Release
            // https://linear.app/prismic/issue/PBD-1412/publish-release-button
            actions={
              <Box gap={8}>
                <ReleaseDate release={release} size="large" />
                <ReleaseDeleteButton release={release} size="large" />
                <ReleasePreview releaseId={release?.id} />
              </Box>
            }
          />
        </Suspense>
      </ErrorBoundary>
      <DocumentListBody tab="upcoming" releaseId={release?.id} />
    </DocumentListBodyLayout>
  )
}

function ReleasePageSkeleton() {
  return (
    <DocumentListProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel />}
        body={
          <DocumentListBodyLayout>
            <ReleasePageHeaderSkeleton />
            <DocumentListBody tab="upcoming" />
          </DocumentListBodyLayout>
        }
      />
    </DocumentListProvider>
  )
}

function useRedirectOnNonExistentRelease() {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { release, routeReleaseId } = useReleaseFromRouteParamId()

  useEffect(() => {
    if (release) return

    navigate("/upcoming")
    showAlert({
      color: "default",
      title: "Release not found",
      subtitle: `The release with id ${routeReleaseId} does not exist. You have been redirected to the upcoming releases page.`
    })
  }, [release, navigate, routeReleaseId, showAlert])
}

function useReleaseFromRouteParamId() {
  const releases = useRepositoryReleases(true)

  const {
    path: { releaseId }
  } = useRouteParams({
    path: { releaseId: z.string() }
  })

  return {
    release: releases.find((release) => release.id === releaseId),
    routeReleaseId: releaseId
  }
}

function renderReleasePageError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching release."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}
